import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import { EstimateModalProvider } from "./components/EstimateModalContext";
import { CompareProvider } from "./contexts/CompareContext";
import CompareDrawer from "./components/CompareDrawer";
import SupaHelpWidget from "./components/supahelp/SupaHelpWidget";

// Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import RebatesPage from "./pages/RebatesPage";
import VicRebateDeadlinePage from "./pages/VicRebateDeadlinePage";
import EOFYBatteryDealPage from "./pages/EOFYBatteryDealPage";
import PackagesPage from "./pages/PackagesPage";
import CQ7PackagePage from "./pages/CQ7PackagePage";
import ContactPage from "./pages/ContactPage";
import TrustPage from "./pages/TrustPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CustomerServicePolicyPage from "./pages/CustomerServicePolicyPage";
import CustomerCareSupportPolicyPage from "./pages/CustomerCareSupportPolicyPage";
import ReferralPage from "./pages/ReferralPage";
import BrandsPage from "./pages/BrandsPage";
import ProjectsPage from "./pages/ProjectsPage";
import CouncilLandingPage from "./pages/CouncilLandingPage";
import EstimatePage from "./pages/EstimatePage";
import SupportPage from "./pages/SupportPage";
import MSBCheckerPage from "./pages/MSBCheckerPage";
import HeatPumpPage from "./pages/HeatPumpPage";
import SupaValueGuaranteePage from "./pages/SupaValueGuaranteePage";
import SupaPromisePage from "./pages/SupaPromisePage";

// Brand Pages
import JinkoSolarPage from "./pages/brands/JinkoSolarPage";
import TeslaPowerwallPage from "./pages/brands/TeslaPowerwallPage";
import DeyePage from "./pages/brands/DeyePage";
import WHESPage from "./pages/brands/WHESPage";
import SigenergyPage from "./pages/brands/SigenergyPage";
import NeopowerPage from "./pages/brands/NeopowerPage";
import EconovaPage from "./pages/brands/EconovaPage";
import MideaPage from "./pages/brands/MideaPage";
import HaierPage from "./pages/brands/HaierPage";
import EmeraldPage from "./pages/brands/EmeraldPage";
import ReclaimPage from "./pages/brands/ReclaimPage";
import SandenPage from "./pages/brands/SandenPage";
import IStorePage from "./pages/brands/IStorePage";
import AquatechPage from "./pages/brands/AquatechPage";
import StiebelPage from "./pages/brands/StiebelPage";
import FoxESSPage from "./pages/brands/FoxESSPage";
import EcactusPage from "./pages/brands/EcactusPage";
import HeatPumpsCategoryPage from "./pages/products/HeatPumpsCategoryPage";
import BatteriesCategoryPage from "./pages/products/BatteriesCategoryPage";
import ProductDetailPage from "./pages/products/ProductDetailPage";
import ComparePage from "./pages/ComparePage";
import GoodWePackagesPage from "./pages/GoodWePackagesPage";
import GoodWePackagePage from "./pages/GoodWePackagePage";
import FoxESSPackagesPage from "./pages/FoxESSPackagesPage";
import DeyePackagesPage from "./pages/DeyePackagesPage";
import DeyePackagePage from "./pages/DeyePackagePage";
import NSWHomeEnergySaverPage from "./pages/NSWHomeEnergySaverPage";
import HeatPumpUpgradePage from "./pages/HeatPumpUpgradePage";
import GameOnClimateActionPage from "./pages/GameOnClimateActionPage";
import { GOODWE_PACKAGES } from "./data/goodwePackages";
import { DEYE_PACKAGES } from "./data/deyePackages";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <CompareProvider>
          <EstimateModalProvider>
          <ScrollToTop />
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/rebates" element={<RebatesPage />} />
          <Route path="/vic-rebate-deadline" element={<VicRebateDeadlinePage />} />
          <Route path="/nsw-home-energy-saver" element={<NSWHomeEnergySaverPage />} />
          <Route path="/game-on-climate-action" element={<GameOnClimateActionPage />} />
          <Route path="/eofy-battery-deal" element={<Navigate to="/packages/eofy-battery-deal" replace />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/eofy-battery-deal" element={<EOFYBatteryDealPage />} />
          <Route path="/packages/:packageSlug" element={<CQ7PackagePage />} />
          {/* GoodWe ESA G20 Battery Packages */}
          <Route path="/goodwe-battery-packages" element={<GoodWePackagesPage />} />
          {GOODWE_PACKAGES.map((p) => (
            <Route key={p.slug} path={`/${p.slug}`} element={<GoodWePackagePage slug={p.slug} />} />
          ))}
          {/* Fox ESS CQ7 Battery Packages (overview) */}
          <Route path="/fox-ess-battery-packages" element={<FoxESSPackagesPage />} />
          {/* Deye AI-W5.1 Battery Packages */}
          <Route path="/deye-battery-packages" element={<DeyePackagesPage />} />
          {DEYE_PACKAGES.map((p) => (
            <Route key={p.slug} path={`/${p.slug}`} element={<DeyePackagePage slug={p.slug} />} />
          ))}
          {/* Catch invalid /deye-*-battery-package slugs → redirect to overview */}
          <Route path="/:deyeSlug" element={<DeyePackagePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/customer-service-policy" element={<CustomerServicePolicyPage />} />
          <Route path="/customer-care-support-policy" element={<CustomerCareSupportPolicyPage />} />
          <Route path="/referral-program" element={<ReferralPage />} />
          <Route path="/get-estimate" element={<EstimatePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/msb" element={<MSBCheckerPage />} />
          <Route path="/heat-pump" element={<HeatPumpPage />} />
          <Route path="/heat-pump-upgrade" element={<HeatPumpUpgradePage />} />
          <Route path="/supa-value-guarantee" element={<SupaValueGuaranteePage />} />
          <Route path="/supa-promise" element={<SupaPromisePage />} />
          <Route path="/brands" element={<BrandsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/brands/jinko-solar" element={<JinkoSolarPage />} />
          <Route path="/brands/tesla-powerwall" element={<TeslaPowerwallPage />} />
          <Route path="/brands/deye" element={<DeyePage />} />
          <Route path="/brands/whes" element={<WHESPage />} />
          <Route path="/brands/sigenergy" element={<SigenergyPage />} />
          <Route path="/brands/neopower" element={<NeopowerPage />} />
          <Route path="/brands/econova" element={<EconovaPage />} />
          <Route path="/brands/midea" element={<MideaPage />} />
          <Route path="/brands/haier" element={<HaierPage />} />
          <Route path="/brands/emerald" element={<EmeraldPage />} />
          <Route path="/brands/reclaim" element={<ReclaimPage />} />
          <Route path="/brands/sanden" element={<SandenPage />} />
          <Route path="/brands/istore" element={<IStorePage />} />
          <Route path="/brands/aquatech" element={<AquatechPage />} />
          <Route path="/brands/stiebel" element={<StiebelPage />} />
          <Route path="/brands/fox-ess" element={<FoxESSPage />} />
          <Route path="/brands/ecactus" element={<EcactusPage />} />
          {/* Product Catalog */}
          <Route path="/products/heat-pumps" element={<HeatPumpsCategoryPage />} />
          <Route path="/products/heat-pumps/:productSlug" element={<ProductDetailPage />} />
          <Route path="/products/batteries" element={<BatteriesCategoryPage />} />
          <Route path="/products/batteries/:productSlug" element={<ProductDetailPage />} />
          <Route path="/compare" element={<ComparePage />} />
          {/* Council Landing Pages */}
          <Route path="/solar/:councilSlug" element={<CouncilLandingPage />} />
        </Routes>
        <SupaHelpWidget />
        <CompareDrawer />
        </EstimateModalProvider>
        </CompareProvider>
      </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
